<template>
  <BasePageCard>
    <div class="freight-table-add-route">
      <div class="freight-table-add-route__header">
        <h2 class="freight-table-add-route__title">Adicionar rota</h2>
        <p v-if="subtitle" class="freight-table-add-route__subtitle">
          {{ subtitle }}
        </p>
      </div>

      <div v-if="loading" class="freight-table-add-route__loading">
        <a-spin />
      </div>

      <div v-else class="freight-table-add-route__sections">
        <FreightTableFormSection
          :index="1"
          title="Identificação"
          subtitle="Configure as informações iniciais da tabela de frete"
          :open="false"
          done
          locked
          @locked-click="onLockedClick"
        />

        <FreightTableFormSection
          :index="2"
          title="Parceiro"
          subtitle="Informe a(s) transportadora(s) e o(s) cliente(s), caso seja um tipo de tabela receita, que irá ser composta."
          :open="false"
          done
          locked
          @locked-click="onLockedClick"
        />

        <FreightTableFormSection
          ref="routesStep"
          :index="3"
          title="Configurações e Rotas"
          subtitle="Cadastre o tipo de cálculo, vigência e valores negociados por rota"
          open
        >
          <a-alert
            v-if="tpCalcDesconhecido"
            type="warning"
            show-icon
            class="freight-table-add-route__blocked"
            message="Não é possível adicionar rota por aqui"
            :description="blockedReason"
          />

          <FreightTableConfigRoutesSection
            v-if="!tpCalcDesconhecido"
            v-model="form"
            lock-header
            :can-add-route="false"
          />
        </FreightTableFormSection>
      </div>

      <div class="freight-table-add-route__actions-bar">
        <a-button
          class="freight-table-add-route__cancel"
          :disabled="saving"
          @click="onCancel"
          >{{ tpCalcDesconhecido ? "Voltar" : "Cancelar" }}</a-button
        >

        <a-tooltip
          v-if="!tpCalcDesconhecido"
          :title="saveHint"
          placement="topRight"
        >
          <span>
            <a-button
              type="primary"
              :loading="saving"
              :disabled="!canSave"
              @click="onSave"
              >Salvar rota</a-button
            >
          </span>
        </a-tooltip>
      </div>
    </div>
  </BasePageCard>
</template>

<script>
import { mapActions } from "vuex";
import BasePageCard from "@/shared/components/BasePageCard.vue";
import FreightTableFormSection from "@/modules/FreightTables/components/Create/FreightTableFormSection.vue";
import FreightTableConfigRoutesSection from "@/modules/FreightTables/components/Create/FreightTableConfigRoutesSection.vue";
import {
  createEmptyConfigRoutesForm,
  createEmptyRoute,
  isRouteValid,
  routeGaps,
} from "@/modules/FreightTables/services/freightTableConfigForm";
import { handleApiError } from "@/shared/utils/handleApiError";
import { describeStoreValidationErrors } from "@/modules/FreightTables/utils/describeStoreValidationErrors";
import {
  FREIGHT_TABLE_LIST_PATH,
  draftPath,
  isFreightTablePath,
} from "@/modules/FreightTables/constants/freightTableRoutes";

export default {
  name: "FreightTableAddRouteView",
  components: {
    BasePageCard,
    FreightTableFormSection,
    FreightTableConfigRoutesSection,
  },
  data() {
    return {
      loading: true,
      saving: false,
      form: createEmptyConfigRoutesForm(),
      routeCount: 0,
      codigo: null,
      descricao: "",
    };
  },
  computed: {
    freightTableId() {
      return Number(this.$route.params.id);
    },
    subtitle() {
      if (this.loading || !this.codigo) return "";
      return this.descricao
        ? `Tabela ${this.codigo} — ${this.descricao}`
        : `Tabela ${this.codigo}`;
    },
    newRoute() {
      return this.form.rota[0];
    },

    tpCalcDesconhecido() {
      return this.form.tp_calc == null;
    },
    blockedReason() {
      return "Não foi possível identificar o tipo de cálculo desta tabela, e ele define as regras do trajeto e das faixas de valor. Use o cadastro completo da tabela para adicionar a rota.";
    },
    canSave() {
      return (
        !this.saving &&
        !this.tpCalcDesconhecido &&
        isRouteValid(this.newRoute, this.form.tp_calc)
      );
    },
    saveHint() {
      if (this.canSave || this.loading) return "";
      const gaps = routeGaps(this.newRoute, this.form.tp_calc);
      const pendencias = Object.keys(gaps).reduce(
        (all, key) => (gaps[key].length ? all.concat(gaps[key]) : all),
        [],
      );
      return pendencias.length ? `Falta preencher — ${pendencias.join("; ")}` : "";
    },
  },
  created() {
    this.load();
  },
  beforeRouteLeave(to, from, next) {
    if (!isFreightTablePath(to.path)) {
      this.resetListState();
    }
    next();
  },
  methods: {
    ...mapActions("freightTables", [
      "fetchFreightTableRouteContext",
      "fetchFreightTableOptions",
      "fetchItineraryOptions",
      "addFreightTableRoute",
      "resetListState",
    ]),
    async load() {
      this.loading = true;
      try {
        const [context] = await Promise.all([
          this.fetchFreightTableRouteContext({
            freightTableId: this.freightTableId,
          }),
          this.fetchFreightTableOptions(),
          this.fetchItineraryOptions(),
        ]);

        if (context.isDraft) {
          this.goToDraft();
          return;
        }

        this.codigo = context.codigo;
        this.descricao = context.descricao;
        this.routeCount = context.routeCount;
        this.form = {
          ...createEmptyConfigRoutesForm(),
          ...context.header,
          rota: [createEmptyRoute(context.routeCount + 1)],
        };
        this.loading = false;
        this.$nextTick(this.scrollToRoutesStep);
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível carregar a tabela de frete",
        );
        this.$router.replace(FREIGHT_TABLE_LIST_PATH);
      }
    },
    goToDraft() {
      this.$notification.info({
        message: "Tabela em rascunho",
        description:
          "Rotas de um rascunho são cadastradas no próprio fluxo de criação.",
      });
      this.$router.replace(draftPath(this.freightTableId));
    },
    scrollToRoutesStep() {
      const step = this.$refs.routesStep;
      if (!step || !step.$el || !step.$el.scrollIntoView) return;

      step.$el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    onLockedClick() {
      this.$warning({
        title: "Etapa indisponível",
        content:
          "Nesta ação apenas a nova rota pode ser cadastrada. Para editar as demais etapas, use o cadastro da tabela.",
        okText: "Entendi",
      });
    },
    onCancel() {
      this.$router.push(FREIGHT_TABLE_LIST_PATH);
    },
    async onSave() {
      if (!this.canSave) return;

      this.saving = true;
      try {
        await this.addFreightTableRoute({
          freightTableId: this.freightTableId,
          route: this.newRoute,
          header: this.form,
          descricao: this.descricao,
          numRotaOffset: this.routeCount,
        });
        this.$notification.success({
          message: "Rota adicionada",
          description: "A nova rota já aparece nos detalhes da tabela.",
        });
        this.$router.push(FREIGHT_TABLE_LIST_PATH);
      } catch (error) {
        const detalhe = describeStoreValidationErrors(error);
        if (detalhe) {
          this.$notification.error({
            message: "Não foi possível adicionar a rota",
            description: detalhe,
            duration: 0,
          });
        } else {
          handleApiError(this, error, "Não foi possível adicionar a rota");
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

@freight-footer-reserve: 100px;

.freight-table-add-route {
  font-family: @font-primary;
  padding: 24px 32px @freight-footer-reserve;

  &__title {
    font-family: @font-primary;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
    margin: 0;
  }

  &__subtitle {
    color: @color-text-light;
    margin: 4px 0 0;
  }

  &__header {
    margin-bottom: 24px;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 64px 0;
  }

  &__blocked {
    margin-bottom: 24px;
  }

  &__actions-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 56px;
    background: #ffffff;
    box-shadow: 0px -2px 16px rgba(0, 0, 0, 0.16);
  }

  &__cancel {
    color: @color-danger-action;
    border-color: @color-danger-action;

    &:hover,
    &:focus {
      color: darken(@color-danger-action, 8%);
      border-color: darken(@color-danger-action, 8%);
    }
  }
}
</style>
